import {
	DetailsRowCompat as DetailsRow,
	IDetailsRowProps,
	IDialogContentProps,
	PersonaSize,
	SeparatorCompat as Separator,
} from 'components/FluentUICompat';
import {EntityListColumn, EntityPage} from 'components';
import {useDialogState} from 'components/Dialogs';
import {BaseDialog} from 'components/Dialogs/BaseDialog';
import {
	renderArrayField,
	renderPersonas,
	renderTranslationField,
	renderDescriptionField,
	renderVkosPersona,
} from 'components/EntityList/ColumnRenderers';
import {LoadWrapper} from 'components/LoadWrapper';
import {ViewAuthorizer} from 'components/ViewAuthorizer';
import {
	ControlledTagPicker,
	ControlledTextField,
	ControlledToggle,
} from 'components/hookForms';
import {ControlledPeoplePicker} from 'components/hookForms/ControlledPeoplePicker';
import {
	KeywordsTooltipTranslationProvider,
	ProviderThatEnablesGettingTooltipsFromContext,
} from 'features/localizedTooltips';
import {mapToRef, omitTypename} from 'helpers';
import React from 'react';
import {Control, useWatch} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import {Keyword, UserRole, VexCluster} from 'types';
import {
	GetAllKeywordsDocument,
	useCreateKeywordMutation,
	useDeleteKeywordMutation,
	useGetAllKeywordsQuery,
	useGetRegulationClustersFormDataQuery,
	useUpdateKeywordMutation,
} from './hooks/keywords.generated';
import {
	useGetAllVexClustersQuery,
	useSyncNewVexClustersMutation,
} from './hooks/vexClusters.generated';
import {numericalSort} from 'helpers/sortHelpers';
import {ARRAY_TYPE, OBJECT_TYPE} from 'components/EntityList/EntityUtils';
import {useGetAllVkosQuery} from './hooks/vko.generated';
import {TranslationFieldKeyword} from 'components/hookForms/TranslationField';
import {DescriptionFieldKeyword} from 'components/hookForms/DescriptionField';
import {SyncKeywordSuccessorsButton} from './components/SyncKeywordSuccessorsButton';

const AdminKeywordsPage: React.FC = () => {
	const {t} = useTranslation('features/adminsection', {
		keyPrefix: 'AdminKeywordsPage',
	});

	const {loading, data} = useGetAllKeywordsQuery();
	const {loading: regulationClustersLoading, data: regulationClustersData} =
		useGetRegulationClustersFormDataQuery();
	const {loading: vexClutersLoading, data: vexClustersData} =
		useGetAllVexClustersQuery();
	const {loading: loadingVkos, data: dataVkos} = useGetAllVkosQuery();
	const [createKeywordMutation] = useCreateKeywordMutation();
	const [updateKeywordMutation] = useUpdateKeywordMutation();
	const [deleteKeywordMutation] = useDeleteKeywordMutation();
	const [syncNewVexClusters] = useSyncNewVexClustersMutation();

	const keywords: Keyword[] = React.useMemo(() => {
		const keywordsUnsorted = data?.keywords ?? [];
		return numericalSort(keywordsUnsorted, 'sortPriority');
	}, [data]);

	const regulationClusters = React.useMemo(
		() => regulationClustersData?.regulationClusters ?? [],
		[regulationClustersData],
	);

	const vexClusters = React.useMemo(
		() => vexClustersData?.vexClusters ?? [],
		[vexClustersData],
	);

	const refetchQueries = [GetAllKeywordsDocument];

	const [
		{hidden, onDismiss},
		showSyncVexClustersDialog,
		hideSyncVexClustersDialog,
	] = useDialogState();
	const [vexClusterSyncState, setVexClusterSyncState] = React.useState<{
		vexClustersToSync: VexCluster[];
		keyword: Keyword | null;
	}>({vexClustersToSync: [], keyword: null});
	const syncVexClustersDialogContentProps: IDialogContentProps = React.useMemo(
		() => ({
			title: t('SyncVexClustersDialogTitle'),
			subText: t('SyncVexClustersDialogSubText', {
				newVexClusters: vexClusterSyncState.vexClustersToSync
					.map(vexCluster => `"${vexCluster.name}"`)
					.join(', '),
				keywordName: vexClusterSyncState.keyword?.name ?? '',
			}),
		}),
		[t, vexClusterSyncState.vexClustersToSync],
	);

	const createKeyword = React.useCallback((keyword: Keyword) => {
		createKeywordMutation({
			variables: {
				input: mapToInput(keyword),
			},
			refetchQueries,
		});
	}, []);

	const updateKeyword = React.useCallback(
		(keyword: Keyword, oldKeyword?: Keyword) => {
			updateKeywordMutation({
				variables: {
					input: mapToInput(keyword),
				},
				refetchQueries,
			}).then(() => {
				const newVexClusters = keyword.vexClusters.filter(
					vexCluster =>
						!oldKeyword?.vexClusters?.some(
							oldVexCluster => oldVexCluster.id === vexCluster.id,
						),
				);
				setVexClusterSyncState({
					vexClustersToSync: newVexClusters,
					keyword,
				});

				if (newVexClusters.length) {
					showSyncVexClustersDialog();
				}
			});
		},
		[showSyncVexClustersDialog, updateKeywordMutation, refetchQueries],
	);

	const deleteKeyword = React.useCallback((id: string) => {
		deleteKeywordMutation({
			variables: {
				input: {id},
			},
			refetchQueries,
		});
	}, []);

	const onConfirmVexClusterSync = React.useCallback(async () => {
		const {vexClustersToSync, keyword} = vexClusterSyncState;
		if (keyword) {
			await syncNewVexClusters({
				variables: {
					input: {
						keywordId: keyword?.id,
						newVexClusterIds: vexClustersToSync.map(
							vexCluster => vexCluster.id,
						),
					},
				},
			});
		}

		hideSyncVexClustersDialog();
	}, [hideSyncVexClustersDialog, vexClusterSyncState]);

	const columns: EntityListColumn[] = React.useMemo(
		() => [
			{
				key: 'translations',
				name: t('Translations'),
				fieldName: 'translations',
				minWidth: 150,
				maxWidth: 150,
				isResizable: true,
				isMultiline: true,
				onRender: renderTranslationField(),
			},
			{
				key: 'descriptions',
				name: t('Descriptions'),
				fieldName: 'descriptions',
				minWidth: 150,
				maxWidth: 200,
				isResizable: true,
				isMultiline: true,
				onRender: renderDescriptionField(),
			},
			{
				key: 'regulationClusters',
				name: t('RegulationClusters'),
				fieldName: 'regulationClusters',
				minWidth: 150,
				maxWidth: 200,
				isResizable: true,
				filterable: true,
				onRender: renderArrayField(),
				dataType: ARRAY_TYPE,
			},
			{
				key: 'vkos',
				name: t('Vkos'),
				fieldName: 'vkos',
				minWidth: 200,
				maxWidth: 200,
				filterable: true,
				onRender: renderVkosPersona(keywords, dataVkos),
				dataType: ARRAY_TYPE,
			},
			{
				key: 'vexClusters',
				name: t('VexClusters'),
				fieldName: 'vexClusters',
				minWidth: 150,
				maxWidth: 200,
				isResizable: true,
				filterable: true,
				onRender: renderArrayField(),
				dataType: ARRAY_TYPE,
			},
			{
				key: 'irrelevantVexClusters',
				name: t('IrrelevantVexClusters'),
				fieldName: 'irrelevantVexClusters',
				minWidth: 150,
				maxWidth: 200,
				isResizable: true,
				filterable: true,
				onRender: renderArrayField(),
				dataType: ARRAY_TYPE,
			},
			{
				key: 'isActive',
				name: t('IsActive'),
				fieldName: 'isActive',
				minWidth: 160,
				maxWidth: 160,
				filterable: true,
				onRender: (item: Keyword) =>
					item.isActive ? t('Active') : t('Inactive'),
				getFilterLabel: (isActive: boolean) =>
					isActive ? t('Active') : t('Inactive'),
				getFilterKey: (isActive: boolean) =>
					isActive ? t('Active') : t('Inactive'),
				getFieldValue: (item: Keyword) => item.isActive,
				dataType: OBJECT_TYPE,
			},
			{
				key: 'successors',
				name: t('Successors'),
				fieldName: 'successors',
				minWidth: 200,
				maxWidth: 200,
				filterable: true,
				onRender: renderArrayField(),
				dataType: ARRAY_TYPE,
			},
			{
				key: 'shApprovers',
				name: t('SHApprovers'),
				fieldName: 'approvers',
				minWidth: 200,
				maxWidth: 200,
				onRender: renderPersonas({size: PersonaSize.size8}),
			},
			{
				key: 'sortPriority',
				name: t('sortPriority'),
				fieldName: 'sortPriority',
				minWidth: 200,
				isResizable: true,
				isMultiline: true,
				filterable: true,
			},
		],
		[keywords, dataVkos],
	);

	const onRenderRow = React.useCallback((props?: IDetailsRowProps) => {
		const customStyles = {
			root: {
				backgroundColor:
					props?.item?.isActive === false ? '#FFCCCC' : undefined,
			},
		};
		return (
			<DetailsRow {...(props as IDetailsRowProps)} styles={customStyles} />
		);
	}, []);

	return (
		<ViewAuthorizer roles={[UserRole.SystemAdministrator]}>
			<LoadWrapper
				loading={loading || regulationClustersLoading || vexClutersLoading}
			>
				<SyncKeywordSuccessorsButton />
				<KeywordsTooltipTranslationProvider>
					<EntityPage
						items={keywords as any}
						entityDisplayName={t('Keywords')}
						createEntity={createKeyword}
						updateEntity={updateKeyword}
						deleteEntity={deleteKeyword}
						additionalColumns={columns}
						renderAdditionalFormElements={control => (
							<FormElements
								control={control}
								regulationClusters={regulationClusters}
								vexClusters={vexClusters}
								keywords={keywords}
							/>
						)}
						hideSortPermanent={false}
						entityListProps={{onRenderRow}}
					/>
				</KeywordsTooltipTranslationProvider>
				<BaseDialog
					styles={{main: {minWidth: 600}}}
					maxWidth={600}
					hidden={hidden}
					onConfirm={onConfirmVexClusterSync}
					onDismiss={onDismiss}
					confirmText={t('SyncVexClustersConfirmText')}
					cancelText={t('SyncVexClustersCancelText')}
					dialogContentProps={syncVexClustersDialogContentProps}
				/>
			</LoadWrapper>
		</ViewAuthorizer>
	);
};

function mapToInput(keyword: Keyword) {
	return {
		id: keyword.id,
		name: keyword?.name,
		regulationClusterRefs: mapToRef(keyword?.regulationClusters),
		vexClusterRefs: mapToRef(keyword?.vexClusters),
		irrelevantVexClusterRefs: mapToRef(keyword?.irrelevantVexClusters),
		vkoRefs: mapToRef(keyword?.vkos),
		approverRefs: mapToRef(keyword?.approvers),
		sortPriority: parseInt(`${keyword.sortPriority}`, 10),
		translations: (keyword.translations ?? []).map(x => omitTypename(x)),
		descriptions: (keyword.descriptions ?? []).map(x => omitTypename(x)),
		isActive: keyword.isActive,
		inactiveReason: keyword.inactiveReason,
		successorRefs: mapToRef(keyword?.successors || []),
	};
}

type FormElementsProps = {
	control: Control<Keyword>;
	regulationClusters: {id: string; name: string}[];
	vexClusters: {id: string; name: string}[];
	keywords: Keyword[];
};

const FormElements: React.FC<FormElementsProps> = ({
	control,
	regulationClusters,
	vexClusters,
	keywords,
}) => {
	const {t} = useTranslation('features/adminsection', {
		keyPrefix: 'AdminKeywordsPage',
	});
	const isActive = useWatch({control, name: 'isActive'});
	return (
		<div>
			<ProviderThatEnablesGettingTooltipsFromContext>
				<ControlledTagPicker
					name={'regulationClusters'}
					label={t('RegulationClusterTagPickerLabel')}
					control={control}
					selectableItems={regulationClusters}
					getKey={item => item.id}
					getName={item => item.name}
				/>
				<ControlledTagPicker
					name={'vexClusters'}
					control={control}
					label={t('VexClustersTagPickerLabel')}
					selectableItems={vexClusters}
					getKey={item => item.id}
					getName={item => item.name}
				/>
				<ControlledTagPicker
					name={'irrelevantVexClusters'}
					control={control}
					label={t('IrrelevantVexClustersTagPickerLabel')}
					selectableItems={vexClusters}
					getKey={item => item.id}
					getName={item => item.name}
				/>
				<ControlledPeoplePicker
					control={control}
					label={t('VkoPeoplePickerLabel')}
					name={'vkos'}
				/>
				<ControlledPeoplePicker
					control={control}
					label={t('SHApprovers')}
					name={'approvers'}
					roles={[UserRole.ShApprover]}
				/>
				<ControlledToggle
					control={control}
					name={'isActive'}
					label={t('IsActive')}
					defaultValue={true}
				/>
				{isActive === false && (
					<>
						<ControlledTextField
							control={control}
							name={'inactiveReason'}
							label={t('InactiveReason')}
							type='text'
							rules={{
								required: t('Required'),
							}}
							required={true}
						/>
						<ControlledTagPicker
							name={'successors'}
							label={t('Successors')}
							control={control}
							selectableItems={keywords}
							getKey={(item: any) => item.id}
							getName={(item: any) => item.name}
						/>
					</>
				)}
				<Separator />
				<TranslationFieldKeyword control={control} />
				<Separator />
				<DescriptionFieldKeyword control={control} />
			</ProviderThatEnablesGettingTooltipsFromContext>
		</div>
	);
};

export default AdminKeywordsPage;
